import React, { useCallback, useEffect, useState } from 'react';
import { deleteDataAPI, getDataAPI, postDataAPI } from '../../Helpers/FetchAPI';
import placeholder from '../../images/pd-resource-square.png';
import { GiftIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SimpleModal } from '../../components/❉molecules/SimpleModal';
import { OneLineInput } from '../../components/⚛️atoms/OneLineInput';
import { Button } from '../../components/⚛️atoms/Button';
import toast from 'react-hot-toast';
import { FullScreenModal } from '../../components/❉molecules/FullScreenModal';
import { WorkbookEditorV2 } from '../../components/WorkbookV2/WorkbookEditorV2';
import { Link } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNav } from '../../Context/Navigation';

const WorkbookListV2 = () => {
  const [workbookList, setWorkbookList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredWorkbooks, setFilteredWorkbooks] = useState([]);

  const [showGiveCreditModal, setShowGiveCreditModal] = useState(false);
  const [giveCreditSku, setGiveCreditSku] = useState('');
  const [expirationDate, setExpirationDate] = useState(null);
  const [noExpiration, setNoExpiration] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workbookToDelete, setWorkbookToDelete] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [workbookToEdit, setWorkbookToEdit] = useState({});

  const { setCurrentPage } = useNav();

  const updateWorkbookList = useCallback(() => {
    setIsLoading(true);
    getDataAPI('/workbook/v2/list').then((response) => {
      setWorkbookList(response.data);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setCurrentPage('Library (V2)');

    updateWorkbookList();
  }, []);

  useEffect(() => {
    setFilteredWorkbooks(
      workbookList.filter((workbook) => {
        return workbook.title.toLowerCase().includes(searchQuery.toLowerCase());
      })
    );
  }, [searchQuery, workbookList]);

  const deleteWorkbook = async () => {
    toast.promise(
      deleteDataAPI('/workbook/v2/' + workbookToDelete).then(() => {
        updateWorkbookList();
        setShowDeleteModal(false);
        return true;
      }),
      {
        loading: 'Deleting...',
        success: 'Successfully deleted.',
        error: 'Error while deleting.'
      }
    );
  };

  const giveCreditModal = async (sku) => {
    await setGiveCreditSku(sku);
    setExpirationDate(null);
    setNoExpiration(true);
    setShowGiveCreditModal(true);
  };

  function giveCredit(e) {
    e.preventDefault();
    if (!e.target.email.value || !e.target.workbook_sku.value || !e.target.quantity.value) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate expiration date is not in the past or today
    if (!noExpiration && expirationDate) {
      const selectedDate = dayjs(expirationDate).startOf('day');
      const today = dayjs().startOf('day');

      if (selectedDate.isBefore(today) || selectedDate.isSame(today)) {
        toast.error('Expiration date must be at least tomorrow', {
          duration: 4000,

        });
        return;
      }
    }

    const data = new FormData(e.target);
    const email = data.get('email');
    const workbook_sku = data.get('workbook_sku');
    const quantity = data.get('quantity');

    const payload = {
      userEmail: email,
      items: [{
        workbookSku: workbook_sku,
        quantity: quantity,
        expiresAt: noExpiration ? null : expirationDate ? dayjs(expirationDate).toISOString() : null
      }]
    };

    toast.promise(
      postDataAPI('/v3/getToken', { email: email }).then((response) => {
        return postDataAPI('/v3/purchaseWorkbook/v2', payload, { headers: { token: response.data.token } });
      }).then(() => {
        setShowGiveCreditModal(false);
        setExpirationDate(null);
        setNoExpiration(true);
      }),
      {
        loading: 'Sending...',
        success: 'Sent!',
        error: 'Failed!'
      }
    );
  }

  const newWorkbook = () => {
    postDataAPI('/workbook/v2', { title: 'New Workbook' }).then(() => {
      updateWorkbookList();
    });
  };

  return (
    <>
      {/*modals*/}
      {showGiveCreditModal && (
        <SimpleModal hideModal={() => setShowGiveCreditModal(false)}>
          <form className="flex w-full flex-col items-center gap-5" onSubmit={(e) => giveCredit(e)}>
            <h2 className="text-2xl font-semibold">Give Credit</h2>

            <OneLineInput
              label="Email"
              name="email"
              type="email"
              onChange={(v) => setSearchQuery(v)}
            ></OneLineInput>

            <label className="flex items-center gap-2">
              Quantity
              <input
                className="ml-2 w-30 rounded-lg border-1 border-gray-200 p-5"
                type="number"
                name="quantity"
                defaultValue={1}
                min={1}
              />
            </label>

            <div className="flex w-full flex-col gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={noExpiration}
                  onChange={(e) => {
                    setNoExpiration(e.target.checked);
                    if (e.target.checked) {
                      setExpirationDate(null);
                    }
                  }}
                  className="size-5 cursor-pointer"
                />
                <span className="text-lg">No Expiration</span>
              </label>

              {!noExpiration && (
                <div className="flex w-full flex-col gap-2">
                  <span className="text-sm font-semibold">Expiration Date</span>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={expirationDate}
                      onChange={(newValue) => setExpirationDate(newValue)}
                      minDate={dayjs().add(1, 'day')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Select expiration date'
                        },
                        popper: {
                          disablePortal: true,
                          sx: {
                            zIndex: 9999
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>
              )}
            </div>

            <input type="hidden" name="workbook_sku" value={giveCreditSku} />
            <Button variant="primary" label="Give" type="submit"></Button>
          </form>
        </SimpleModal>
      )}
      {showDeleteModal && (
        <SimpleModal hideModal={() => setShowDeleteModal(false)}>
          <div className="flex w-full flex-col items-center gap-5">
            <span>Are you sure you want to delete this workbook?</span>
            <div className="flex items-center gap-5">
              <Button onClick={() => setShowDeleteModal(false)} label="No"></Button>
              <Button variant="primary" onClick={deleteWorkbook} label="Yes"></Button>
            </div>
          </div>
        </SimpleModal>
      )}
      {showEditModal && (
        <FullScreenModal
          closeModal={() => {
            setShowEditModal(false);
            updateWorkbookList();
          }}
        >
          <WorkbookEditorV2 workbook={workbookToEdit}></WorkbookEditorV2>
        </FullScreenModal>
      )}
      {/*end modals*/}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between ">
        <OneLineInput className="w-1/3" placeholder="Search..." name="search" type="search" onChange={(v) => setSearchQuery(v)}></OneLineInput>
        <Button className="w-120 self-end" variant="primary" label="Create New Workbook" onClick={() => newWorkbook()}>
          <PlusCircleIcon className="ml-2 size-10"></PlusCircleIcon>
        </Button>
        </div>
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex animate-pulse items-center justify-between rounded-xl p-10 shadow-md"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-xl border-gray-200 p-2 shadow-md">
                    <div className="size-30 rounded-xl bg-gray-200" />
                  </div>
                  <div className="h-6 w-48 rounded-lg bg-gray-200" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-28 rounded-lg bg-gray-200" />
                  <div className="h-8 w-16 rounded-lg bg-gray-200" />
                  <div className="h-8 w-20 rounded-lg bg-gray-200" />
                </div>
              </div>
            ))
          : filteredWorkbooks.map((workbook) => (
          <div className="flex items-center justify-between rounded-xl p-10 shadow-md" key={workbook._id}>
            <div className="flex items-center gap-3">
              <div className="rounded-xl border-gray-200 p-2 shadow-md">
                <img
                  src={workbook.cover_image ? `${process.env.REACT_APP_API_URL}/${workbook.cover_image}` : placeholder}
                  alt="Cover"
                  className="size-30 rounded-xl"
                />
              </div>
              <span>{workbook.title}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="flex items-center gap-2 rounded-lg p-2 shadow-sm hover:bg-gray-100"
                onClick={() => giveCreditModal(workbook.sku)}
              >
                <GiftIcon className="size-10"></GiftIcon>
                <span className="text-xl">Give Credit</span>
              </button>
              <Link
                to={`/workbook/v2/${workbook._id}`}
                className="flex items-center gap-2 rounded-lg p-2 shadow-sm hover:bg-gray-100"
              >
                <PencilSquareIcon className="size-10"></PencilSquareIcon>
                <span className="text-xl">Edit</span>
              </Link>
              <button
                className="flex items-center gap-2 rounded-lg p-2 shadow-sm hover:bg-gray-100"
                onClick={async () => {
                  setWorkbookToDelete(workbook._id);
                  setShowDeleteModal(true);
                }}
              >
                <TrashIcon className="size-10"></TrashIcon>
                <span className="text-xl">Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default WorkbookListV2;
